import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { BuilderMessageState, setNumInteractionsProcessed } from "@/store/slices/builderMessageSlice";
import type { RootState } from "@/store";
import { Play } from "lucide-react";

interface NutMessageToggleProps {
  onClick: () => void;
}

export function NutMessageToggle({ onClick }: NutMessageToggleProps) {
  if (!import.meta.env.VITE_ENABLE_APP_MESSAGES) {
    return null;
  }

  const dispatch = useAppDispatch();
  const builderMessageState: BuilderMessageState | undefined = useAppSelector((state: RootState) => 
    'builderMessage' in state ? (state as RootState & { builderMessage: BuilderMessageState }).builderMessage : undefined
  );

  const { lastMessageResponse, numInteractionsProcessed } = builderMessageState ?? {};
  const hasPendingInteraction = (numInteractionsProcessed ?? 0) < (lastMessageResponse?.interactionInstructions?.interactions.length ?? 0);

  const handleInteractionClick = () => {
    const nextInteraction = lastMessageResponse?.interactionInstructions?.interactions[numInteractionsProcessed ?? 0];
    if (!nextInteraction) {
      return;
    }

    const element = document.querySelector(`[data-testid="${nextInteraction.dataTestId}"]`) as HTMLElement;

    if (!element) {
      console.log('Interaction element not found', nextInteraction);
      return;
    }

    // Perform the interaction based on kind
    if (nextInteraction.kind === "Click") {
      element.click();
    } else if (nextInteraction.kind === "InputText" && nextInteraction.text !== undefined) {
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        element.focus();
        element.select();
        
        // Use native value setter to trigger React's onChange
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement?.prototype || HTMLInputElement.prototype,
          'value'
        )?.set;
        
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(element, nextInteraction.text);
        } else {
          element.value = nextInteraction.text;
        }
        
        // Dispatch proper input event
        const inputEvent = new InputEvent('input', {
          bubbles: true,
          cancelable: true,
          inputType: 'insertText',
          data: nextInteraction.text,
        });
        element.dispatchEvent(inputEvent);
        
        // Dispatch change event
        const changeEvent = new Event('change', {
          bubbles: true,
          cancelable: true,
        });
        element.dispatchEvent(changeEvent);
      }
    }

    // Advance to the next interaction
    dispatch(setNumInteractionsProcessed((numInteractionsProcessed ?? 0) + 1));
  };

  const title = hasPendingInteraction ? 'Next interaction' : 'Get help';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          onClick={() => hasPendingInteraction ? handleInteractionClick() : onClick()}
          className="p-2 w-10 h-10 rounded-md border bg-secondary hover:bg-accent border-border transition-colors"
          title={title}
          data-nut-message-toggle
        >
          {hasPendingInteraction ? (
            <Play className="text-foreground h-5 w-5" />
          ) : (
            <span className="text-foreground text-lg font-semibold">?</span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{title}</TooltipContent>
    </Tooltip>
  );
}

export default NutMessageToggle;
